import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { config } from '../constants';
import { usePaywallOptional } from '../paywall/PaywallProvider';

const url = config.url.API_URL;

export default function TeacherAccessInvite({ onSessionRefresh }) {
  const { code } = useParams();
  const navigate = useNavigate();
  const paywall = usePaywallOptional();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      setErrorCode('');

      try {
        const [inviteResponse, authResponse] = await Promise.all([
          fetch(`${url}/api/teacher-access/${encodeURIComponent(code)}`, { credentials: 'include' }),
          fetch(`${url}/record/checkAuth`, { credentials: 'include' }),
        ]);

        const inviteData = await inviteResponse.json().catch(() => ({}));
        const authData = await authResponse.json().catch(() => ({}));

        if (!inviteResponse.ok) {
          setError(inviteData.message || 'Invite not found.');
          setErrorCode(inviteData.code || 'INVITE_NOT_FOUND');
          return;
        }

        setInvite(inviteData.invite);
        setAuthenticated(Boolean(authData.authenticated));
        setUserEmail(authData.email || '');
      } catch (loadError) {
        setError('Unable to load invite.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [code]);

  const handleRedeem = async () => {
    setRedeeming(true);
    setError('');
    setErrorCode('');

    try {
      const response = await fetch(`${url}/api/teacher-access/${encodeURIComponent(code)}/redeem`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.message || `Unable to redeem invite (${response.status}).`);
        setErrorCode(data.code || '');
        return;
      }

      onSessionRefresh?.({
        userRoles: data.userRoles,
        access: data.access,
        showTeacherAccessWelcome: data.showTeacherAccessWelcome,
      });
      await paywall?.refreshAccess?.();

      setSuccessMessage(data.message || 'Teacher access is now active.');
    } catch (redeemError) {
      console.error('Teacher invite redeem failed:', redeemError);
      setError('Unable to redeem invite. The request timed out or the server was unreachable.');
    } finally {
      setRedeeming(false);
    }
  };

  const switchAccountPath = `/logout?returnTo=${encodeURIComponent(`/teacher-access/${code}`)}`;

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Loading teacher access invite…</p>
      </Container>
    );
  }

  if (errorCode === 'INVITE_NOT_FOUND') {
    return (
      <Container className="py-5">
        <Alert variant="danger">This teacher access invite was not found.</Alert>
      </Container>
    );
  }

  if (invite?.status === 'expired') {
    return (
      <Container className="py-5">
        <Alert variant="warning">This teacher access invite has expired.</Alert>
      </Container>
    );
  }

  if (invite?.status === 'revoked') {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          This teacher access invite has been revoked.
          {!authenticated && (
            <div className="mt-2 mb-0">
              If you received a newer invite, sign in with the allowlisted Google account and we will check for an active invite for you.
            </div>
          )}
        </Alert>
        {!authenticated && (
          <Button
            variant="primary"
            onClick={() => navigate('/loginWithGoogle', { state: { from: `/teacher-access/${code}` } })}
          >
            Sign in with Google
          </Button>
        )}
      </Container>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: '720px' }}>
      <Card>
        <Card.Body className="p-4">
          <h1 className="h3 mb-3">Teacher access invite</h1>
          {invite?.label && <p className="text-muted">{invite.label}</p>}

          {successMessage ? (
            <Alert variant="success">
              {successMessage}
              <div className="mt-3">
                <Button variant="primary" onClick={() => navigate('/manageClasses')}>
                  Go to Manage Classes
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              {!authenticated && (
                <Alert variant="info">
                  Sign in with the Google account that received this invite to activate teacher access.
                  <div className="mt-3">
                    <Button
                      variant="primary"
                      onClick={() => navigate('/loginWithGoogle', { state: { from: `/teacher-access/${code}` } })}
                    >
                      Sign in with Google
                    </Button>
                  </div>
                </Alert>
              )}

              {authenticated && (
                <>
                  <p>
                    Signed in as <strong>{userEmail}</strong>.
                  </p>
                  <p>
                    Redeeming will grant complimentary teacher access and let you create and manage classes.
                  </p>
                  {error && (
                    <Alert variant="danger">
                      {error}
                      {errorCode === 'EMAIL_NOT_ALLOWLISTED' && (
                        <div className="mt-3">
                          <Button as={Link} to={switchAccountPath} variant="outline-primary">
                            Switch account
                          </Button>
                        </div>
                      )}
                    </Alert>
                  )}
                  <Button variant="success" onClick={handleRedeem} disabled={redeeming}>
                    {redeeming ? 'Activating…' : 'Activate teacher access'}
                  </Button>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
